import React, { useState } from 'react';
import { useStore } from '../store/StoreContext';
import { useToast } from '../hooks/useToast';
import { format } from 'date-fns';
import { IconPlus, IconUsers } from '../components/icons';
import CrewModal from '../components/modals/CrewModal';
import CrewDaysOffModal from '../components/modals/CrewDaysOffModal';
import { getCrewSkillSummary } from '../utils/dispatchHelpers';
import { DOW_LABELS } from '../lib/crewOff';
import { Button, EntityCard, EntityAvatar, StatusPill, initialsOf, cardGridCls, PageHeader, Toolbar, FilterChips, EmptyState, pageWrapCls } from '../components/ui';

const CREW_COLORS = ['#dc2626', '#ea580c', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'];

function CrewPage() {
    const { crew, jobs, saveCrew, deactivateCrew, permanentlyDeleteCrew, availability, setCrewAvailability } = useStore();
    const [modal, setModal] = useState(null);
    const [daysOffModal, setDaysOffModal] = useState(null); // crew member id
    const [filter, setFilter] = useState('all');
    const { show: showToast } = useToast();

    const handleSave = async (form) => { await saveCrew(form); showToast(form.id ? 'Updated' : 'Added'); };
    const handleDelete = async (id) => { await deactivateCrew(id); showToast('Crew member moved to inactive'); };
    const getJobCount = (id) => jobs.filter(j => (j.crew_ids || []).includes(id)).length;

    const activeCrew = crew.filter(m => m.active !== false);
    const inactiveCrew = crew.filter(m => m.active === false);
    const baseCrew = filter === 'inactive' ? inactiveCrew : activeCrew;
    const filtered = filter === 'all' ? activeCrew : filter === 'inactive' ? inactiveCrew : filter === 'movers' ? activeCrew.filter(m => (m.skill_moving || 0) > 0) : filter === 'packers' ? activeCrew.filter(m => (m.skill_packing || 0) > 0) : activeCrew;

    // Header summary + chip counts (page-headers.md: counts, not prose).
    const moverCount = activeCrew.filter(m => (m.skill_moving || 0) > 0).length;
    const packerCount = activeCrew.filter(m => (m.skill_packing || 0) > 0).length;
    // "Lead" = moving skill 4–5 (Jr Crew Leader / Crew Leader — MOVING_SKILL_TITLES), which is what the cards show; the role column is the login role.
    const leadCount = activeCrew.filter(m => Number(m.skill_moving || 0) >= 4).length;
    const summary = [
        `${crew.length} member${crew.length === 1 ? '' : 's'}`,
        `${activeCrew.length} active`,
        `${leadCount} lead${leadCount === 1 ? '' : 's'}`,
    ].join(' · ');
    const chipItems = [
        { key: 'all', label: 'All', count: activeCrew.length },
        { key: 'movers', label: 'Movers', count: moverCount, color: '#2563eb' },
        { key: 'packers', label: 'Packers', count: packerCount, color: '#db2777' },
        ...(inactiveCrew.length > 0 ? [{ key: 'inactive', label: 'Inactive', count: inactiveCrew.length, color: '#94a3b8' }] : []),
    ];

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Crew"
                summary={summary}
                help={<>
                    <p>Every crew member on the roster, active or not. Cards show each person's skill levels, phone, weekly days off and how many jobs they've been on.</p>
                    <p className="mt-2">Click a card to edit. Deactivating keeps the history and moves the member under the Inactive chip; Days Off sets the weekly schedule and one-off requests the dispatch board honours.</p>
                </>}
                actions={
                    <Button variant="primary" leftIcon={<IconPlus size={18}/>} onClick={() => setModal('new')}>
                        Add Crew Member
                    </Button>
                }
            />

            <Toolbar>
                <FilterChips ariaLabel="Filter crew" items={chipItems} value={filter} onChange={setFilter} />
            </Toolbar>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">Total Crew</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{activeCrew.length}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">Movers</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{activeCrew.filter(m => (m.skill_moving || 0) > 0).length}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">Packers</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{activeCrew.filter(m => (m.skill_packing || 0) > 0).length}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500 uppercase font-medium">On Jobs Today</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{new Set(jobs.filter(j => j.move_date === format(new Date(), 'yyyy-MM-dd')).flatMap(j => j.crew_ids || [])).size}</p>
                </div>
            </div>

            {filtered.length > 0 && <div className={cardGridCls}>
                {filtered.map(member => {
                    const isActive = member.active !== false;
                    const daysOff = member.recurring_days_off || [];
                    return (
                        // One card recipe — lists.md §4 (EntityCard owns padding, radius, hover, muted).
                        <EntityCard
                            key={member.id}
                            avatar={<EntityAvatar text={initialsOf(member.name) || '?'} color={member.display_color || '#3b82f6'} />}
                            title={member.name}
                            subtitle={getCrewSkillSummary(member)}
                            badge={<StatusPill label={isActive ? 'Active' : 'Inactive'} color={isActive ? '#16a34a' : '#64748b'} />}
                            facts={[
                                { label: 'Phone', value: member.phone || '—', mono: true },
                                { label: 'Days off', value: daysOff.length ? daysOff.map(d => DOW_LABELS[d]).join(', ') : 'None' },
                                { label: 'Jobs', value: `${getJobCount(member.id)} jobs` },
                            ]}
                            muted={!isActive}
                            onClick={() => setModal(member)}
                            footer={<>
                                <Button variant="tertiary" size="sm" onClick={(e) => { e.stopPropagation(); setModal(member); }}>Edit</Button>
                                {/* Days Off (Susie 2026-08-06) — weekly schedule +
                                    specific requests with a yearly tally. */}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    title={daysOff.length ? `Off every ${daysOff.map(d => DOW_LABELS[d]).join(', ')}` : 'Days off & weekly schedule'}
                                    onClick={(e) => { e.stopPropagation(); setDaysOffModal(member.id); }}>
                                    🗓 Days Off{daysOff.length ? ` · ${daysOff.map(d => DOW_LABELS[d]).join(',')}` : ''}
                                </Button>
                            </>}
                        >
                            {/* Skill Bars */}
                            <div className="space-y-2">
                                {(member.skill_moving || 0) > 0 && <div>
                                    <span className="text-[10px] text-slate-400 font-medium">Moving Skill Lvl</span>
                                    <div className="flex items-end gap-1 mt-1">{[1,2,3,4,5].map(lvl => <div key={lvl} style={{height: `${8 + (lvl - 1) * 3}px`, width: '100%'}} className={`rounded-sm ${lvl <= member.skill_moving ? 'bg-red-500' : 'bg-slate-200'}`}></div>)}</div>
                                </div>}
                                {(member.skill_packing || 0) > 0 && <div>
                                    <span className="text-[10px] text-slate-400 font-medium">Packing Skill Lvl</span>
                                    <div className="flex items-end gap-1 mt-1">{[1,2,3,4,5].map(lvl => <div key={lvl} style={{height: `${8 + (lvl - 1) * 3}px`, width: '100%'}} className={`rounded-sm ${lvl <= member.skill_packing ? 'bg-pink-500' : 'bg-slate-200'}`}></div>)}</div>
                                </div>}
                                {(member.skill_moving || 0) === 0 && (member.skill_packing || 0) === 0 && <div>
                                    <span className="text-[10px] text-slate-400 font-medium">Skill Lvl</span>
                                    <div className="flex items-end gap-1 mt-1">{[1,2,3,4,5].map(lvl => <div key={lvl} style={{height: `${8 + (lvl - 1) * 3}px`, width: '100%'}} className={`rounded-sm ${lvl <= (member.skill_level || 1) ? 'bg-red-500' : 'bg-slate-200'}`}></div>)}</div>
                                </div>}
                            </div>
                        </EntityCard>
                    );
                })}
            </div>}
            {filtered.length === 0 && (
                crew.length === 0 ? (
                    <EmptyState
                        icon={<IconUsers size={28} />}
                        title="No crew members yet"
                        hint="Add your first crew member to start assigning them to jobs on the dispatch board."
                        action={<Button size="sm" leftIcon={<IconPlus size={16}/>} onClick={() => setModal('new')}>Add Crew Member</Button>}
                    />
                ) : (
                    <EmptyState
                        icon={<IconUsers size={28} />}
                        title="No crew members match"
                        hint="Nobody on the roster fits this filter."
                        action={<Button variant="tertiary" size="sm" onClick={() => setFilter('all')}>Clear filters</Button>}
                    />
                )
            )}
            {modal && <CrewModal member={modal === 'new' ? null : modal} takenColors={crew.filter(c => c.active !== false && c.id !== (modal === 'new' ? null : modal?.id)).map(c => c.display_color).filter(Boolean)} onClose={() => setModal(null)} onSave={async (form) => { await handleSave(form); setModal(null); }} onDelete={async (id) => { await handleDelete(id); setModal(null); }} onPermDelete={async (id) => { await permanentlyDeleteCrew(id); showToast('Permanently deleted'); setModal(null); }} />}
            {daysOffModal && (() => {
                // Re-resolve from the store each render so saves reflect live.
                const m = crew.find(c => c.id === daysOffModal);
                return m ? (
                    <CrewDaysOffModal
                        member={m}
                        availability={availability}
                        setCrewAvailability={setCrewAvailability}
                        saveCrew={saveCrew}
                        onClose={() => setDaysOffModal(null)}
                        showToast={showToast}
                    />
                ) : null;
            })()}
        </div>
    );
}

export default CrewPage;
